import { useCallback, useEffect, useMemo, useState } from 'react';
import GamePlayer, { cleanGame, gameThumb, type Game } from '../../components/GamePlayer';
import Loading from '../../components/Loading';
import ShopTeacher from '../../components/ShopTeacher';
import { GEAR } from '../../game/gear';
import { classDrafts, EGGS, MAKE_COST, type GameDraft } from '../../lib/shop';
import { useClassroom } from '../../state/Classroom';
import Fortress from '../student/Tower';

// ─────────────────────────────────────────────────────────────────────────────
// The teacher's view of the student Shop (/shop):
//   Games        games sent in for approval, and the ones in the arcade
//   In progress  what each student is building right now (ocean_game_drafts)
//   For sale     what students can spend sand dollars on, and the prices
// ─────────────────────────────────────────────────────────────────────────────

type Tab = 'games' | 'drafts' | 'items' | 'fortress';

const ago = (iso: string) => {
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function TeacherShop() {
  const { active } = useClassroom();
  const [tab, setTab] = useState<Tab>('games');
  if (!active) return <div className="notice">Pick a class first.</div>;
  return (
    <div className="teacher-shop">
      <h1 style={{ marginTop: 0 }}>🛍️ Shop</h1>
      <p className="muted">What your students see in their Shop: games they make for each other, and things to spend sand dollars on.</p>
      <div className="seg" role="tablist" style={{ maxWidth: 560 }}>
        <button type="button" role="tab" aria-selected={tab === 'games'} className={tab === 'games' ? 'on' : ''} onClick={() => setTab('games')}>🕹️ Games</button>
        <button type="button" role="tab" aria-selected={tab === 'drafts'} className={tab === 'drafts' ? 'on' : ''} onClick={() => setTab('drafts')}>🛠️ In progress</button>
        <button type="button" role="tab" aria-selected={tab === 'items'} className={tab === 'items' ? 'on' : ''} onClick={() => setTab('items')}>🛍️ For sale</button>
        <button type="button" role="tab" aria-selected={tab === 'fortress'} className={tab === 'fortress' ? 'on' : ''} onClick={() => setTab('fortress')}>🏰 Fortress</button>
      </div>
      {tab === 'games' && <ShopTeacher cid={active.id} />}
      {tab === 'drafts' && <Drafts cid={active.id} />}
      {tab === 'items' && <ForSale />}
      {tab === 'fortress' && <Fortress cid={active.id} language={active.language} look={null} balance={0} setBalance={() => {}} teacher />}
    </div>
  );
}

function Drafts({ cid }: { cid: string }) {
  const { students } = useClassroom();
  const [drafts, setDrafts] = useState<GameDraft[] | null>(null);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState<{ game: Game; title: string } | null>(null);

  const load = useCallback(async () => {
    try { setDrafts(await classDrafts(cid)); setError(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load the games in progress.'); }
  }, [cid]);
  useEffect(() => { void load(); }, [load]);

  if (playing) return <GamePlayer game={playing.game} look={null} title={playing.title} onClose={() => setPlaying(null)} />;
  if (error && !drafts) return <div className="notice bad">{error}</div>;
  if (!drafts) return <Loading what="games in progress" />;

  const name = (id: string) => students.find(s => s.user_id === id)?.display_name ?? 'A student';
  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="panel-head"><h3>🛠️ Games students are building</h3><button type="button" className="small" onClick={() => void load()}>↻ Refresh</button></div>
      <p className="small muted">Saved as they work (a couple of seconds after each change). Once a student sends one in, it shows on the Games tab.</p>
      {drafts.length === 0 ? <p className="muted small">Nobody is building a game right now.</p> : (
        <div className="draft-grid">
          {drafts.map(d => <DraftCard key={`${d.student_id}-${d.mode}`} d={d} who={name(d.student_id)} onPlay={(game, title) => setPlaying({ game, title })} />)}
        </div>
      )}
    </div>
  );
}

function DraftCard({ d, who, onPlay }: { d: GameDraft; who: string; onPlay: (g: Game, title: string) => void }) {
  const game = useMemo(() => cleanGame(d.content), [d.content]);
  const thumb = useMemo(() => (game ? gameThumb(game, 320) : ''), [game]);
  const title = d.title.trim() || '(no name yet)';
  return (
    <div className="draft-card">
      {thumb ? <img className="level-thumb big" src={thumb} alt="" /> : <div className="img-attach-wait">?</div>}
      <div><strong>{who}</strong> · {d.mode === 'race' ? '🏎️ race' : '🕹️ platformer'}</div>
      <div className="small" dir="auto">{title}</div>
      <div className="small muted">Last change {ago(d.updated_at)}</div>
      {game && <button type="button" className="small" onClick={() => onPlay(game, `${who}: ${title}`)}>▶ Try it</button>}
    </div>
  );
}

function ForSale() {
  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="panel-head"><h3>🛍️ What students can buy</h3></div>
      <p className="small muted">Prices are in sand dollars. Gear is bought once and kept in every class.</p>
      <h4>🥚 Eggs</h4>
      <div className="sale-grid">
        {EGGS.map(e => <div key={e.id} className="sale-item"><span>🥚 {e.name}</span><span className="small muted">{e.blurb}</span><strong>🪙 {e.cost}</strong></div>)}
      </div>
      {(['hat', 'face', 'trail'] as const).map(slot => (
        <div key={slot}>
          <h4>{slot === 'hat' ? '🎩 Hats' : slot === 'face' ? '🕶️ Face' : '✨ Trails'}</h4>
          <div className="sale-grid">
            {GEAR.filter(g => g.slot === slot).map(g => <div key={g.id} className="sale-item"><span>{g.icon} {g.name}</span><strong>🪙 {g.cost}</strong></div>)}
          </div>
        </div>
      ))}
      <h4>🕹️ Making a game</h4>
      <p className="small">Sending a game in costs 🪙 {MAKE_COST} (not given back if you turn it down). Playing a classmate’s game costs the price you set; nobody earns from it. Students never earn sand dollars from games: only from you and from class work.</p>
    </div>
  );
}
